import React, { useState, useEffect } from 'react';

const EditPeopleLimitModal = () => {
  const [modal, setModal] = useState(null);
  // modal = { eventId, currentParticipants, currentLimit }
  const [inputValue, setInputValue] = useState('');
  const [noLimit, setNoLimit] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handleOpen = (e) => {
      const { currentLimit } = e.detail;
      setModal(e.detail);
      setInputValue('');
      setNoLimit(currentLimit === null);
      setError('');
      setSaving(false);
    };
    window.addEventListener('openEditPeopleLimitModal', handleOpen);
    return () => window.removeEventListener('openEditPeopleLimitModal', handleOpen);
  }, []);

  if (!modal) return null;

  const { eventId, currentParticipants, currentLimit } = modal;

  // Validation logic
  const validate = (value, isNoLimit) => {
    if (isNoLimit) {
      return currentLimit !== null ? '' : 'Already set to no limit';
    }
    if (value === '') return null; // empty = invalid but no error shown
    const num = parseInt(value);
    if (isNaN(num)) return 'Please enter a valid number';
    if (num <= currentParticipants) return `Limit must be greater than current participants (${currentParticipants})`;
    if (num < 2) return 'Minimum limit is 2 people';
    if (num > 200) return 'Maximum limit is 200 people';
    if (num === currentLimit) return 'Please enter a different value or cancel';
    return '';
  };

  const currentError = validate(inputValue, noLimit);
  const canSave = saving ? false :
    noLimit ? (currentLimit !== null) :
    (inputValue !== '' && currentError === '');

  const handleNoLimitChange = (checked) => {
    setNoLimit(checked);
    if (checked) {
      setInputValue('');
      setError('');
    }
  };

  const handleInputChange = (val) => {
    setInputValue(val);
    const err = validate(val, false);
    setError(val !== '' && err ? err : '');
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);

    const newLimit = noLimit ? null : parseInt(inputValue);

    // Close modal
    setModal(null);

    // Update UI in the event details panel (same as before)
    const countElement = document.querySelector('[data-participants-count]');
    if (countElement) {
      countElement.textContent = newLimit === null
        ? `${currentParticipants} / ∞`
        : `${currentParticipants} / ${newLimit}`;
    }

    const panel = document.getElementById('eventDetailsPanel');
    if (panel) {
      panel.dataset.tempPeopleLimit = newLimit === null ? 'null' : newLimit;
    }

    if (window.updateSaveButtonState) {
      window.updateSaveButtonState();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          width: '90%',
          maxWidth: '400px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          animation: 'slideUp 0.3s ease-out'
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 600, color: '#202124' }}>
          Edit People Limit
        </h3>

        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#5f6368' }}>
          Current participants: <strong>{currentParticipants}</strong>
        </p>

        {/* Input */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>
            People limit<span style={{ color: '#000', marginLeft: '2px' }}>*</span>{' '}
            <span style={{ color: '#5f6368', fontWeight: 400 }}>(2 - 200)</span>
          </label>
          <input
            type="number"
            min={currentParticipants + 1}
            max={200}
            value={inputValue}
            disabled={noLimit}
            placeholder="e.g., 10"
            onChange={(e) => handleInputChange(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: `2px solid ${error ? '#ea4335' : '#dadce0'}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              background: noLimit ? '#f5f5f5' : 'white',
              color: noLimit ? '#9e9e9e' : '#202124',
              cursor: noLimit ? 'not-allowed' : 'text',
              outline: 'none'
            }}
          />
          {error && (
            <div style={{ color: '#ea4335', fontSize: '12px', marginTop: '6px' }}>
              {error}
            </div>
          )}

          {/* No Limit checkbox */}
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="editNoLimitCheckbox"
              checked={noLimit}
              onChange={(e) => handleNoLimitChange(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer', margin: 0, accentColor: '#008fa1' }}
            />
            <label
              htmlFor="editNoLimitCheckbox"
              style={{ fontSize: '14px', color: '#5f6368', cursor: 'pointer', userSelect: 'none' }}
            >
              No Limit
            </label>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleSave}
            disabled={!canSave}
            onMouseOver={(e) => { if (canSave) { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '3px 3px 8px rgba(0,0,0,0.5)'; } }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '2px 2px 6px rgba(0,0,0,0.4)'; }}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: 'white',
              color: 'black',
              border: '2px solid rgb(97,97,97)',
              borderRadius: '24px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: canSave ? 'pointer' : 'not-allowed',
              opacity: canSave ? 1 : 0.5,
              transition: 'all 0.3s ease',
              boxShadow: '2px 2px 6px rgba(0,0,0,0.4)'
            }}
          >
            Save
          </button>
          <button
            onClick={() => setModal(null)}
            onMouseOver={(e) => e.currentTarget.style.background = '#e8eaed'}
            onMouseOut={(e) => e.currentTarget.style.background = '#f1f3f4'}
            style={{
              flex: 1,
              padding: '12px 20px',
              background: '#f1f3f4',
              color: '#5f6368',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Cancel
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default EditPeopleLimitModal;